/**
 * Permission codes the expense screens declare an interest in
 * (`internal/expense/permissions.go`, design C3 §6.1).
 *
 * `expense.claim.create` ⊗ `expense.claim.approve` is a declared SoD pair, and
 * §6.1 says why it is needed *in addition to* the approvals engine's own
 * self-approval guard: a claim can be created **for** an employee by a finance
 * user, and the pair is what stops that user approving it.
 *
 * Standing rule 9 (§6.7) applies to everything a `RoleEmployee` can reach here:
 * `expense.claim.read` answers "may this person read expense claims?" when the
 * question is "may they read **this** one". The server filters to the caller's
 * own employee row unless they also hold `expense.claim.approve`; these screens
 * never send an employee id that would widen the result.
 */

export const EXPENSE_PERMISSIONS = {
  claimRead: 'expense.claim.read',
  claimCreate: 'expense.claim.create',
  claimApprove: 'expense.claim.approve',
  /** Materialise the AP invoice — the act that makes the claim payable (§10.4). */
  claimPost: 'expense.claim.post',

  /**
   * Route an approved claim to payroll (or back), mark an advance for recovery from pay, and
   * read the payroll inclusions ledger (FT15-B48).
   */
  claimSettlePayroll: 'expense.claim.settle_payroll',

  policyRead: 'expense.policy.read',
  policyManage: 'expense.policy.manage',

  travelRead: 'expense.travel.read',
  travelManage: 'expense.travel.manage',

  cardRead: 'expense.card.read',
  cardManage: 'expense.card.manage',

  /** Read companies to scope a claim, a policy or a card account. */
  companyRead: 'org.company.read',
  /** Read the chart of accounts behind a category's expense account. */
  accountRead: 'org.account.read',
  /** Pick a card account's holder (FT3-U10). The server validates the id. */
  employeeRead: 'hr.employee.read',
  /** Pick a card account's issuer supplier (FT3-U10). */
  vendorRead: 'ap.vendor.read',
} as const

export type ExpensePermission = (typeof EXPENSE_PERMISSIONS)[keyof typeof EXPENSE_PERMISSIONS]
